// ── RelatedEventsLink ─────────────────────────────────────────────────────────
// Cross-link into Event Inspector pre-filled with source=connected&account=<sourceAccount>
// and a 7-day window around the transaction's created timestamp.

import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import type { Transaction } from "@/application/money/types";
import { Button } from "@/components/ui/base/button";
import { ROUTES } from "@/routes";

interface RelatedEventsLinkProps {
  transaction: Transaction;
}

export function RelatedEventsLink({ transaction: tx }: RelatedEventsLinkProps) {
  const navigate = useNavigate();
  const hasAccount = tx.sourceAccount != null && tx.sourceAccount !== "";

  function buildEventsUrl(): string {
    if (!hasAccount || tx.created == null) {
      return ROUTES.EVENTS;
    }
    const from = tx.created;
    const to = tx.created + 7 * 86400;
    const params = new URLSearchParams({
      source: "connected",
      account: tx.sourceAccount,
      from: String(from),
      to: String(to),
    });
    return `${ROUTES.EVENTS}?${params.toString()}`;
  }

  return (
    <div className="bg-card rounded-lg border p-5">
      <h2 className="text-foreground mb-3 text-sm font-semibold">
        Related Events
      </h2>
      <Button
        variant="outline"
        size="sm"
        disabled={!hasAccount}
        title={
          !hasAccount
            ? "No connected account recorded for this transaction."
            : undefined
        }
        onClick={() => navigate(buildEventsUrl())}
        className="gap-1.5"
      >
        View events for this account
        <ArrowUpRight className="h-4 w-4" />
      </Button>
      {!hasAccount && (
        <p className="text-subtle-foreground mt-2 text-xs">
          No connected account recorded for this transaction.
        </p>
      )}
    </div>
  );
}
